import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Asterisk, Upload, Loader2, AlertTriangle, CheckCircle2, XCircle } from "lucide-react";
import { when } from "@/components/claude/ClaudeBadge";
import { uploadToBid } from "@/lib/chunkedUpload";

function FileRow({ f }) {
  const Icon = f.status === "done" ? CheckCircle2 : f.status === "failed" ? XCircle : Loader2;
  const tone = f.status === "done" ? "text-emerald-600" : f.status === "failed" ? "text-rose-500" : "text-[#003E87] animate-spin";
  const label = { uploading: `Uploading ${f.pct}%`, processing: "Filing…", done: "Added", failed: f.error || "Failed" }[f.status];
  return (
    <div className="flex items-center gap-2 px-3 py-2 text-sm" data-testid={`claude-upload-file-${f.id}`}>
      <Icon className={`w-4 h-4 shrink-0 ${tone}`} />
      <span className="flex-1 min-w-0 truncate text-slate-700">{f.name}</span>
      <span className="text-xs text-slate-400 shrink-0">{label}</span>
    </div>
  );
}

export default function ClaudeUpload() {
  const { token } = useParams();
  const inputRef = useRef(null);
  const [link, setLink] = useState(null);
  const [err, setErr] = useState("");
  const [files, setFiles] = useState([]);
  const [drag, setDrag] = useState(false);

  useEffect(() => {
    api.get(`/claude/upload-links/${token}`).then((r) => setLink(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "This upload link isn't valid."));
  }, [token]);

  const patch = (id, p) => setFiles((fs) => fs.map((f) => (f.id === id ? { ...f, ...p } : f)));
  const addFiles = async (list) => {
    for (const file of Array.from(list || [])) {
      const id = `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
      setFiles((fs) => [...fs, { id, name: file.name, status: "uploading", pct: 0 }]);
      try { await uploadToBid(link.bid_id, file, { folderId: link.folder_id || null, onState: (p) => patch(id, p) }); patch(id, { status: "done" }); }
      catch (e) { patch(id, { status: "failed", error: e.response?.data?.detail || e.message }); }
    }
  };
  const onDrop = (e) => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); };

  return (
    <div className="max-w-xl mx-auto px-6 py-12" data-testid="claude-upload-page">
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-3"><Asterisk className="w-4 h-4 text-[#D97757]" /><h1 className="font-semibold text-black">Add files for Claude</h1></div>
        {err ? (
          <div data-testid="claude-upload-error" className="flex gap-2 text-sm text-slate-700"><AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />{err}</div>
        ) : !link ? (
          <Loader2 className="w-5 h-5 animate-spin text-[#003E87]" />
        ) : (
          <>
            <div className="text-sm text-slate-700">Files go to the documents of <Link to={`/bids/${link.bid_id}`} className="text-[#003E87] hover:underline font-medium" data-testid="claude-upload-bid">{link.bid_name}</Link>.</div>
            {link.note && <div className="text-sm text-slate-500 mt-1 italic" data-testid="claude-upload-note">“{link.note}”</div>}
            <div className="text-xs text-slate-400 mt-1">Link works until {when(link.expires_at)}. Tell Claude when you're done.</div>
            <div data-testid="claude-upload-drop" onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={onDrop}
              className={`mt-4 rounded-xl border-2 border-dashed px-6 py-10 text-center transition-colors ${drag ? "border-[#003E87] bg-[#003E87]/5" : "border-slate-200"}`}>
              <Upload className="w-6 h-6 text-slate-400 mx-auto mb-2" />
              <div className="text-sm text-slate-600">Drop files here</div>
              <Button size="sm" variant="outline" className="mt-3" data-testid="claude-upload-pick" onClick={() => inputRef.current?.click()}>Choose files</Button>
              <input ref={inputRef} type="file" multiple className="hidden" data-testid="claude-upload-input" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
            </div>
            {files.length > 0 && <div className="mt-4 divide-y divide-slate-100 rounded-lg border border-slate-200" data-testid="claude-upload-files">{files.map((f) => <FileRow key={f.id} f={f} />)}</div>}
          </>
        )}
      </Card>
    </div>
  );
}
